/**
 * Course Recommendation Card Component
 * Enhanced card with module dropdown, thumbs up/down, and start course functionality
 */

import React, { useState, useEffect, useCallback } from 'react';
import {
  Clock,
  Target,
  CheckCircle2,
  Circle,
  TrendingUp,
  ThumbsUp,
  ThumbsDown,
  Play,
  ChevronDown,
  ChevronUp,
  List,
  Loader2,
} from 'lucide-react';
import { RecommendedCourse } from '../../services/skillsAssessment';
import { courseService } from '../../services/firebase/courses';
import { learningPlansService } from '../../services/firebase/learningPlans';

interface CourseRecommendationCardProps {
  course: RecommendedCourse;
  index: number;
  onLike: (courseId: string) => void;
  onDislike: (courseId: string) => void;
  onStartCourse: (courseId: string, moduleId?: string) => void;
  className?: string;
}

interface ModuleInfo {
  id: string;
  title: string;
  type: string;
  order: number;
  estimatedDuration: number;
  isRequired: boolean;
}

export const CourseRecommendationCard: React.FC<CourseRecommendationCardProps> = ({
  course,
  index,
  onLike,
  onDislike,
  onStartCourse,
  className = '',
}) => {
  const [isExpanded, setIsExpanded] = useState(false);
  const [showModuleDropdown, setShowModuleDropdown] = useState(false);
  const [modules, setModules] = useState<ModuleInfo[]>([]);
  const [isLoadingModules, setIsLoadingModules] = useState(false);
  const [userFeedback, setUserFeedback] = useState<'like' | 'dislike' | null>(null);
  const [isInLearningPlan, setIsInLearningPlan] = useState(false);
  const [courseDescription, setCourseDescription] = useState<string | null>(null);
  const [showMilestones, setShowMilestones] = useState(false);
  const [isDisliking, setIsDisliking] = useState(false);

  /**
   * Check if course is in user's learning plan
   */
  const checkLearningPlanStatus = useCallback(async () => {
    try {
      const inPlan = await learningPlansService.isCourseInLearningPlan(course.courseId);
      setIsInLearningPlan(inPlan);
      if (inPlan) {
        setUserFeedback('like');
      }
    } catch (error) {
      console.error('Error checking learning plan status:', error);
    }
  }, [course.courseId]);

  /**
   * Load course description from Firestore
   */
  const loadCourseDescription = useCallback(async () => {
    try {
      const courseData = await courseService.getCourse(course.courseId);
      if (courseData) {
        setCourseDescription(courseData.description || courseData.shortDescription || null);
      }
    } catch (error) {
      console.error('Error loading course description:', error);
    }
  }, [course.courseId]);
  
  useEffect(() => {
    checkLearningPlanStatus();
    loadCourseDescription();
  }, [checkLearningPlanStatus, loadCourseDescription]);

  /**
   * Load course modules
   */
  const loadModules = async () => {
    if (modules.length > 0) return; // Already loaded
    
    try {
      setIsLoadingModules(true);
      const courseModules = await courseService.getCourseModules(course.courseId);
      
      const moduleInfo: ModuleInfo[] = courseModules.map(module => ({
        id: module.id,
        title: module.title,
        type: module.type,
        order: module.order,
        estimatedDuration: module.estimatedDuration,
        isRequired: module.isRequired,
      }));
      
      setModules(moduleInfo.sort((a, b) => a.order - b.order));
    } catch (error) {
      console.error('Error loading modules:', error);
    } finally {
      setIsLoadingModules(false);
    }
  };

  /**
   * Handle thumbs up
   */
  const handleThumbsUp = async () => {
    try {
      if (userFeedback === 'like') return; // Already liked
      
      await learningPlansService.addCourseToLearningPlan(
        course.courseId,
        course.courseTitle,
        'recommendation'
      );
      
      setUserFeedback('like');
      setIsInLearningPlan(true);
      onLike(course.courseId);
    } catch (error) {
      console.error('Error adding course to learning plan:', error);
    }
  };

  /**
   * Handle thumbs down
   */
  const handleThumbsDown = async () => {
    try {
      if (userFeedback === 'dislike' || isDisliking) return; // Already disliked or in progress
      
      setIsDisliking(true);
      await learningPlansService.removeCourseFromLearningPlan(course.courseId, 'recommendation');
      
      setUserFeedback('dislike');
      setIsInLearningPlan(false);
      onDislike(course.courseId);
    } catch (error) {
      console.error('Error removing course from learning plan:', error);
      setIsDisliking(false); // Re-enable on error
    }
  };

  /**
   * Handle start course
   */
  const handleStartCourse = (moduleId?: string) => {
    onStartCourse(course.courseId, moduleId);
  };

  /**
   * Toggle module dropdown
   */
  const toggleModuleDropdown = async () => {
    if (!showModuleDropdown) {
      await loadModules();
    }
    setShowModuleDropdown(!showModuleDropdown);
  };

  /**
   * Calculate progress
   */
  const calculateProgress = () => {
    if (!course.milestones || course.milestones.length === 0) return 0;
    const completedMilestones = course.milestones.filter(m => m.isCompleted).length;
    return (completedMilestones / course.milestones.length) * 100;
  };

  /**
   * Format duration
   */
  const formatDuration = (minutes: number) => {
    const hours = Math.floor(minutes / 60);
    const mins = minutes % 60;
    if (hours > 0) {
      return `${hours}h ${mins > 0 ? `${mins}m` : ''}`;
    }
    return `${mins}m`;
  };

  return (
    <div className={`bg-white border border-gray-200 rounded-lg p-6 hover:shadow-lg transition-shadow ${className}`}>
      {/* Course Header */}
      <div className="flex items-start justify-between mb-4">
        <div className="flex-1">
          <div className="flex items-center space-x-3 mb-2">
            <div className="w-8 h-8 bg-gradient-to-r from-blue-500 to-teal-500 rounded-full flex items-center justify-center text-white font-semibold text-sm">
              {index + 1}
            </div>
            <h3 className="text-lg font-semibold text-gray-900">{course.courseTitle}</h3>
            {isInLearningPlan && (
              <span className="bg-green-100 text-green-800 text-xs font-medium px-2 py-1 rounded-full">
                In Learning Plan
              </span>
            )}
          </div>
          {/* Display match reasons if available */}
          {course.matchReasons && course.matchReasons.length > 0 ? (
            <div className="mb-3">
              <p className="text-sm font-medium text-gray-700 mb-2">Why this course is recommended for you:</p>
              <ul className="space-y-1">
                {course.matchReasons.map((reason, idx) => (
                  <li key={idx} className="flex items-start space-x-2 text-sm text-gray-600">
                    <span className="text-blue-500 mt-1">•</span>
                    <span>{reason}</span>
                  </li>
                ))}
              </ul>
            </div>
          ) : course.reasoning ? (
            <p className="text-gray-600 mb-3">{course.reasoning}</p>
          ) : null}
          {courseDescription && (
            <div className="bg-blue-50 border-l-4 border-blue-500 p-3 mb-3">
              <p className="text-sm text-gray-700">{courseDescription}</p>
            </div>
          )}
          <div className="flex items-center space-x-4 text-sm text-gray-500">
            <div className="flex items-center space-x-1">
              <Clock className="w-4 h-4" />
              <span>{course.estimatedCompletionWeeks} weeks</span>
            </div>
            {course.milestones && course.milestones.length > 0 && (
              <button
                onClick={() => setShowMilestones(!showMilestones)}
                className="flex items-center space-x-1 hover:text-blue-600 transition-colors"
              >
                <Target className="w-4 h-4" />
                <span>{course.milestones.length} milestones</span>
                {showMilestones ? <ChevronUp className="w-4 h-4" /> : <ChevronDown className="w-4 h-4" />}
              </button>
            )}
          </div>
          
          {/* Collapsible Milestones */}
          {showMilestones && course.milestones && course.milestones.length > 0 && (
            <div className="mt-3 space-y-2">
              {course.milestones.map((milestone, idx) => (
                <div key={idx} className="flex items-start space-x-2 text-sm">
                  {milestone.isCompleted ? (
                    <CheckCircle2 className="w-4 h-4 text-green-500 flex-shrink-0 mt-0.5" />
                  ) : (
                    <Circle className="w-4 h-4 text-gray-400 flex-shrink-0 mt-0.5" />
                  )}
                  <div className="flex-1">
                    <p className="font-medium text-gray-900">{milestone.milestoneTitle}</p>
                    {milestone.description && (
                      <p className="text-gray-600 text-xs mt-0.5">{milestone.description}</p>
                    )}
                  </div>
                </div>
              ))}
            </div>
          )}
        </div>
        <div className="text-right ml-4">
          <div className="text-2xl font-bold text-blue-600">
            {Math.round(calculateProgress())}%
          </div>
          <p className="text-sm text-gray-600">Complete</p>
        </div>
      </div>

      {/* Progress Bar */}
      <div className="mb-4">
        <div className="w-full bg-gray-200 rounded-full h-1.5">
          <div
            className="bg-gradient-to-r from-blue-500 to-teal-500 h-1.5 rounded-full transition-all duration-300"
            style={{ width: `${calculateProgress()}%` }}
          />
        </div>
      </div>

      {/* Action Buttons */}
      <div className="flex flex-col gap-4 sm:flex-row sm:items-center sm:justify-between">
        <button
          onClick={() => handleStartCourse()}
          className="order-1 sm:order-2 flex items-center justify-center space-x-2 px-4 py-2 bg-gradient-to-r from-blue-500 to-teal-500 text-white rounded-lg hover:from-blue-600 hover:to-teal-600 transition-all duration-200 w-full sm:w-auto"
        >
          <Play className="w-4 h-4" />
          <span>Start Course</span>
        </button>

        <div className="flex flex-wrap items-center gap-2 sm:gap-3 order-2 sm:order-1">
          {/* Thumbs Up/Down */}
          <button
            onClick={handleThumbsUp}
            className={`p-2 rounded-lg transition-colors ${
              userFeedback === 'like'
                ? 'bg-green-100 text-green-600'
                : 'bg-gray-100 text-gray-600 hover:bg-green-50 hover:text-green-600'
            }`}
            title="Add to Learning Plan"
          >
            <ThumbsUp className="w-4 h-4" />
          </button>
          
          <button
            onClick={handleThumbsDown}
            disabled={isDisliking}
            className={`p-2 rounded-lg transition-colors ${
              isDisliking
                ? 'bg-gray-200 text-gray-400 cursor-not-allowed'
                : userFeedback === 'dislike'
                ? 'bg-red-100 text-red-600'
                : 'bg-gray-100 text-gray-600 hover:bg-red-50 hover:text-red-600'
            }`}
            title={isDisliking ? 'Removing course...' : 'Not Interested'}
          >
            {isDisliking ? (
              <Loader2 className="w-4 h-4 animate-spin" />
            ) : (
              <ThumbsDown className="w-4 h-4" />
            )}
          </button>

          {/* Module Dropdown */}
          <div className="relative w-full sm:w-auto">
            <button
              onClick={toggleModuleDropdown}
              className="flex items-center justify-between sm:justify-center space-x-2 px-3 py-2 bg-gray-100 text-gray-700 rounded-lg hover:bg-gray-200 transition-colors w-full sm:w-auto"
            >
              <List className="w-4 h-4" />
              <span>Modules</span>
              {showModuleDropdown ? (
                <ChevronUp className="w-4 h-4" />
              ) : (
                <ChevronDown className="w-4 h-4" />
              )}
            </button>

            {/* Dropdown Menu */}
            {showModuleDropdown && (
              <div className="absolute top-full left-0 mt-1 w-80 bg-white border border-gray-200 rounded-lg shadow-lg z-10">
                <div className="p-3 border-b border-gray-200">
                  <h4 className="font-medium text-gray-900">Course Modules</h4>
                </div>
                <div className="max-h-60 overflow-y-auto">
                  {isLoadingModules ? (
                    <div className="p-4 text-center text-gray-500">
                      Loading modules...
                    </div>
                  ) : modules.length > 0 ? (
                    modules.map((module) => (
                      <button
                        key={module.id}
                        onClick={() => {
                          handleStartCourse(module.id);
                          setShowModuleDropdown(false);
                        }}
                        className="w-full text-left p-3 hover:bg-gray-50 transition-colors border-b border-gray-100 last:border-b-0"
                      >
                        <div className="flex items-center justify-between">
                          <div className="flex-1">
                            <h5 className="font-medium text-gray-900 text-sm">
                              {module.title}
                            </h5>
                            <div className="flex items-center space-x-2 mt-1">
                              <span className="text-xs text-gray-500 capitalize">
                                {module.type}
                              </span>
                              <span className="text-xs text-gray-400">•</span>
                              <span className="text-xs text-gray-500">
                                {formatDuration(module.estimatedDuration)}
                              </span>
                              {module.isRequired && (
                                <>
                                  <span className="text-xs text-gray-400">•</span>
                                  <span className="text-xs text-blue-600">Required</span>
                                </>
                              )}
                            </div>
                          </div>
                          <Play className="w-4 h-4 text-gray-400" />
                        </div>
                      </button>
                    ))
                  ) : (
                    <div className="p-4 text-center text-gray-500">
                      No modules available
                    </div>
                  )}
                </div>
              </div>
            )}
          </div>
        </div>
      </div>

      {/* Expanded Details */}
      {isExpanded && (
        <div className="mt-6 pt-6 border-t border-gray-200">
          <h4 className="font-semibold text-gray-900 mb-4 flex items-center">
            <TrendingUp className="w-4 h-4 mr-2" />
            Learning Milestones
          </h4>
          <div className="space-y-3">
            {(course.milestones || []).map((milestone, milestoneIndex) => (
              <div
                key={milestoneIndex}
                className="flex items-start space-x-3 p-3 bg-gray-50 rounded-lg"
              >
                <div className="mt-0.5">
                  {milestone.isCompleted ? (
                    <CheckCircle2 className="w-5 h-5 text-green-500" />
                  ) : (
                    <Circle className="w-5 h-5 text-gray-400" />
                  )}
                </div>
                <div className="flex-1">
                  <h5 className={`font-medium ${milestone.isCompleted ? 'text-green-700' : 'text-gray-900'}`}>
                    {milestone.milestoneTitle}
                  </h5>
                  <p className="text-sm text-gray-600 mt-1">{milestone.description}</p>
                  <p className="text-xs text-gray-500 mt-1">
                    Target: {milestone.targetDate ? new Date(milestone.targetDate).toLocaleDateString() : 'Not set'}
                  </p>
                </div>
              </div>
            ))}
          </div>
        </div>
      )}

      {/* Toggle Details Button */}
      <button
        onClick={() => setIsExpanded(!isExpanded)}
        className="w-full mt-4 py-2 text-sm text-gray-600 hover:text-gray-800 transition-colors flex items-center justify-center space-x-1"
      >
        <span>{isExpanded ? 'Hide Details' : 'Show Details'}</span>
        {isExpanded ? (
          <ChevronUp className="w-4 h-4" />
        ) : (
          <ChevronDown className="w-4 h-4" />
        )}
      </button>
    </div>
  );
};
